import { AppLayout } from "@/components/layout/AppLayout";
import { useDecisionMetrics, Opportunity } from "@/hooks/useDecisionMetrics";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TrendingUp, ChevronDown, ChevronUp, X, Zap } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";

const effortColors: Record<string, string> = {
  low: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
  medium: "bg-amber-500/10 text-amber-600 border-amber-500/20",
  high: "bg-red-500/10 text-red-600 border-red-500/20",
};

const OpportunityCard = ({ opp, rank }: { opp: Opportunity; rank: number }) => {
  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-start gap-4">
          <div className="flex items-center justify-center h-8 w-8 rounded-full bg-primary/10 text-primary text-sm font-bold shrink-0">
            {rank}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-semibold text-foreground leading-tight">{opp.title}</h3>
              <Badge variant="outline" className={`shrink-0 ${effortColors[opp.effort]}`}>
                {opp.effort} effort
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-1">{opp.reason}</p>
            <div className="flex items-center gap-1.5 mt-2">
              <Zap className="h-3.5 w-3.5 text-primary" />
              <span className="text-xs font-medium text-primary">{opp.estimatedImpact}</span>
            </div>

            <div className="flex items-center gap-2 mt-3">
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-8"
                onClick={() => setExpanded(!expanded)}
              >
                {expanded ? <ChevronUp className="h-3.5 w-3.5 mr-1" /> : <ChevronDown className="h-3.5 w-3.5 mr-1" />}
                {expanded ? "Less" : "What triggered this"}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-8 text-muted-foreground"
                onClick={() => setDismissed(true)}
              >
                <X className="h-3.5 w-3.5 mr-1" />
                Dismiss
              </Button>
            </div>

            {expanded && (
              <div className="mt-4 space-y-3 border-t pt-3">
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5">What data triggered this</p>
                  <p className="text-sm text-foreground">{opp.reason}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5">Actions that might help</p>
                  <ul className="text-sm text-foreground space-y-1">
                    {opp.title.toLowerCase().includes("expense") && (
                      <>
                        <li>• Review line-by-line expenses for this vehicle</li>
                        <li>• Look for recurring costs that could be negotiated</li>
                        <li>• Compare with similar vehicles in your fleet</li>
                      </>
                    )}
                    {opp.title.toLowerCase().includes("reprice") && (
                      <>
                        <li>• Review comparable listings in your market</li>
                        <li>• Consider adjusting weekly/monthly discounts</li>
                        <li>• Test a small price reduction for 2-4 weeks</li>
                      </>
                    )}
                    {opp.title.toLowerCase().includes("investigate") && (
                      <>
                        <li>• Review the specific expenses from last month</li>
                        <li>• Determine if the spike was a one-time event</li>
                        <li>• Set a reminder to check next month</li>
                      </>
                    )}
                  </ul>
                </div>
                {opp.vehicleId && (
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" asChild>
                      <Link to="/app/expenses">View expenses</Link>
                    </Button>
                    <Button variant="outline" size="sm" asChild>
                      <Link to="/app/earnings">View earnings</Link>
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const ProfitOpportunities = () => {
  const { opportunities, isLoading, vehicleData } = useDecisionMetrics();

  return (
    <AppLayout>
      <div className="p-4 md:p-8 space-y-6 max-w-3xl">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Profit Improvement Opportunities</h1>
          <p className="text-muted-foreground mt-1">
            Ranked by effort — low-effort changes first. Estimated impacts are rough ranges, not promises.
          </p>
        </div>

        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-28 w-full" />
            ))}
          </div>
        ) : vehicleData.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <TrendingUp className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <h3 className="font-semibold text-foreground">Need vehicle data</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Add vehicles and import trips and expenses to see improvement opportunities.
              </p>
            </CardContent>
          </Card>
        ) : opportunities.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <TrendingUp className="h-10 w-10 text-primary mx-auto mb-3" />
              <h3 className="font-semibold text-foreground">No opportunities flagged right now</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Your fleet metrics look solid. HostCaptain will surface opportunities as more data comes in.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {opportunities.map((opp, i) => (
              <OpportunityCard key={opp.id} opp={opp} rank={i + 1} />
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default ProfitOpportunities;
